import { useEffect } from "react";

import { useUser } from "../user";
import { usePreferencesFirestorePersistence } from "./use-preferences-firestore-persistence";
import { usePreferencesFirestoreSwr } from "./use-preferences-firestore-swr";
import { usePreferencesGAUserProperties } from "./use-preferences-ga-user-properties";
import { usePreferencesLocalPersistence } from "./use-preferences-local-persistence";
import { usePreferencesLocalSwr } from "./use-preferences-local-swr";

export function usePreferencesEffect(): void {
  useReportPreferencesToGA();
  useSyncFirestoreAndLocal();
}

function useReportPreferencesToGA(): void {
  const { data: preferences } = usePreferencesLocalSwr();
  const { updatePreferences: updateGAPreferences } =
    usePreferencesGAUserProperties();

  useEffect(() => {
    if (preferences === undefined || preferences === null) return;
    updateGAPreferences(preferences);
  }, [preferences, updateGAPreferences]);
}

/**
 * Reads are local, so the account's preferences reach this device only here. Firestore
 * wins wholesale — the account is the authority, and the local copy is a device-level
 * store that may well belong to whoever signed in last.
 */
function useSyncFirestoreAndLocal(): void {
  const { user } = useUser();
  const { data: firestoreData, mutate: mutateFirestore } =
    usePreferencesFirestoreSwr();
  const { data: localData, mutate: mutateLocal } = usePreferencesLocalSwr();
  const { setPreferences: setLocalPreferences } =
    usePreferencesLocalPersistence();
  const { setPreferences: setFirestorePreferences } =
    usePreferencesFirestorePersistence();

  useEffect(() => {
    if (firestoreData !== null) {
      void mutateLocal(
        setLocalPreferences(firestoreData).then(() => firestoreData),
        { optimisticData: firestoreData, revalidate: false },
      );
      return;
    }

    if (localData !== null) {
      void mutateFirestore(
        setFirestorePreferences(user.id, localData).then(() => localData),
        { optimisticData: localData, revalidate: false },
      );
    }
  }, [
    user.id,
    firestoreData,
    setLocalPreferences,
    mutateLocal,
    localData,
    mutateFirestore,
    setFirestorePreferences,
  ]);
}
